/**
 * Auto Assign dialog shell: progress, errors, footer actions, and the active review step.
 *
 * @remarks
 * The steps/ folders mirror the visible workflow. The state/ hook coordinates the
 * draft and persistence; this entry component only composes the UI.
 *
 * @packageDocumentation
 */
import React from 'react';
import ReactDOM from 'react-dom';
import {assignmentPlanForLegs, needsAssignmentReview} from '../../../../common/flight-leg/assignment-plan';
import {FlightLeg} from '../../../../schema';
import {getInitialReturnToOrigin} from './state/itinerary';
import {Alert, Button, Modal, Steps, Typography} from 'antd';
import {AutoAssignReviewOptions, useAutoAssignReview} from './state/use-auto-assign-review';
import {RouteReviewStep, VisitChoicesStep, FinalReviewStep} from './steps';
import './review-dialog.less';

/** Route review inputs, save callbacks, and passenger/cargo display names. */
export interface AutoAssignReviewDialogProps extends AutoAssignReviewOptions {
    labels: Map<string, string>;
    /** Describes the operation that proposed this route. */
    title?: string;
    /** Action label when a caller reviews a proposal before persisting it elsewhere. */
    confirmText?: string;
    /** Whether final review can return to an owning route editor. */
    showEditRoute?: boolean;
}

/** Composes the step UI around the review hook's state and semantic actions. */
export function AutoAssignReviewDialog(props: AutoAssignReviewDialogProps) {
    const flow = useAutoAssignReview(props);
    const {step, showSteps, saving, error, saveFailed, canSave, continueReview, save} = flow;
    return (
        <Modal
            visible
            title={props.title || 'Review auto-assigned route'}
            width={900}
            className="auto-assign-flow"
            closable={!saving}
            maskClosable={false}
            keyboard={!saving}
            onCancel={flow.cancel}
            footer={
                <div className="flow-footer">
                    <div className="flow-footer__buttons">
                        <Button disabled={saving} onClick={flow.back}>
                            {step > 0 ? 'Back' : 'Cancel'}
                        </Button>
                        {canSave ? (
                            <Button type="primary" loading={saving} onClick={save}>
                                {saveFailed
                                    ? 'Retry save'
                                    : props.confirmText || 'Save route and assignments'}
                            </Button>
                        ) : (
                            <Button
                                type="primary"
                                disabled={!flow.canContinue}
                                onClick={continueReview}
                            >
                                {flow.needsVisitChoices
                                    ? 'Continue to visit choices'
                                    : 'Continue to final review'}
                            </Button>
                        )}
                    </div>
                </div>
            }
        >
            {showSteps && (
                <div className="flow-navigation">
                    <div className="flow-wayfinding">
                        <Steps current={step} size="small">
                            <Steps.Step title="Review route" />
                            <Steps.Step
                                title="Choose visits"
                                description={`${flow.completedChoices} of ${flow.totalChoices} selections made`}
                            />
                            <Steps.Step title="Final review" />
                        </Steps>
                        <Typography.Text type="secondary">Step {step + 1} of 3</Typography.Text>
                    </div>
                </div>
            )}
            {error && <Alert type="error" showIcon message={error} />}

            {step === 0 && (
                <RouteReviewStep flow={flow} hasRoutePolicy={!!props.currentRoutePlan} />
            )}
            {step === 1 && (
                <VisitChoicesStep flow={flow} names={props.names} labels={props.labels} />
            )}
            {step === 2 && (
                <FinalReviewStep
                    flow={flow}
                    names={props.names}
                    labels={props.labels}
                    showEditRoute={props.showEditRoute}
                />
            )}
        </Modal>
    );
}

/**
 * Opens an isolated review dialog and resolves when it is saved or cancelled.
 *
 * @returns Whether the route and assignments were saved successfully.
 */
export function reviewAutoAssign(
    props: Omit<AutoAssignReviewDialogProps, 'onClose'>
): Promise<boolean> {
    return new Promise((resolve) => {
        const host = document.createElement('div');
        document.body.appendChild(host);

        const close = (saved: boolean) => {
            resolve(saved);
            window.setTimeout(() => {
                ReactDOM.unmountComponentAtNode(host);
                host.remove();
            }, 0);
        };

        ReactDOM.render(<AutoAssignReviewDialog {...props} onClose={close} />, host);
    });
}

/** Review data for an authored route; persistence remains supplied by the route-save session. */
export interface EditedRouteReviewOptions {
    legs: FlightLeg[];
    labels: Map<string, string>;
    isCurrent: () => boolean;
    save: (legs: FlightLeg[]) => Promise<void>;
}

/**
 * Reviews an authored route using the same grouped/individual UI as Auto Assign.
 *
 * @remarks
 * The route drawer owns stop edits. This dialog cannot reorder or extend that
 * route; Edit route returns to the drawer without saving. The supplied save
 * callback runs before the dialog closes, retaining choices after a failure.
 */
export async function reviewEditedRoute(
    options: EditedRouteReviewOptions
): Promise<FlightLeg[] | undefined> {
    return reviewAuthoredRoute(options, {title: 'Review modified route'});
}

/**
 * Reviews assignments for leg actions without persisting their proposal.
 *
 * @param always - Also review already-resolved assignments when true.
 * @returns The confirmed legs, or undefined when the operation is cancelled.
 * Callers remain responsible for applying the confirmed proposal.
 */
export async function reviewPlannedLegs(
    legs: FlightLeg[],
    always = false,
    labels = new Map<string, string>()
): Promise<FlightLeg[] | undefined> {
    const plan = assignmentPlanForLegs(legs);
    if (!plan || (!always && !needsAssignmentReview(plan))) return legs;

    return reviewAuthoredRoute(
        {legs, labels, isCurrent: () => true, save: async () => {}},
        {title: 'Review assignments', confirmText: 'Use assignments', showEditRoute: false}
    );
}

/** Shares authored-route presentation while preserving each caller's persistence contract. */
async function reviewAuthoredRoute(
    options: EditedRouteReviewOptions,
    presentation: Pick<AutoAssignReviewDialogProps, 'title' | 'confirmText' | 'showEditRoute'>
): Promise<FlightLeg[] | undefined> {
    const plan = assignmentPlanForLegs(options.legs);
    if (!plan) throw new Error('Could not prepare route assignments.');

    const names = new Map<string, string>();
    options.legs.forEach((leg) => {
        names.set(leg.departureID, leg.departure || leg.departureID);
        names.set(leg.destinationID, leg.destination || leg.destinationID);
    });

    let reviewed: FlightLeg[] | undefined;
    const saved = await reviewAutoAssign({
        initialPlan: plan,
        returnToOrigin: getInitialReturnToOrigin(options.legs),
        routeEditing: 'external',
        ...presentation,
        names,
        labels: options.labels,
        isCurrent: options.isCurrent,
        save: async (proposal) => {
            await options.save(proposal.legs);
            reviewed = proposal.legs;
        },
    });

    return saved ? reviewed : undefined;
}
